import { asConstraintId, type Constraint } from '@/domain/constraints';
import { type EntitySource } from '@/domain/construction';
import { vec } from '@/domain/math';
import {
  addConstraint,
  addEntity,
  CONSTRUCTION_STYLE,
  createDocument,
  createEntity,
  DEFAULT_STYLE,
  type EntityId,
  LineStyle,
  PointStyle,
  type SceneDocument,
  type Style,
  withStyle,
} from '@/domain/model';

/**
 * A worked construction, built by hand.
 *
 * **Temporary scaffolding.** The drawing tools arrive in M7; until then there is no way
 * for a user to create geometry, and a renderer with nothing to render cannot be
 * verified by looking at it. This document exercises the parts of M5 that are hardest to
 * be confident about from unit tests alone — a deep dependency chain, an undefined-value
 * path, a locus point, and every painter.
 *
 * It lives in `bootstrap` because that is the one place allowed to assemble concrete
 * things. Delete this file when the tools land.
 *
 * The construction is the classic one: a triangle, its circumcircle, its medians meeting
 * at the centroid, and a point locked to the circle. Drag nothing — but zoom in as far
 * as you like and the circumcircle still passes exactly through all three vertices,
 * because it is *derived* rather than drawn.
 *
 * A second, separate figure exercises Tier 2: a quadrilateral whose opposite sides are
 * constrained parallel and whose two remaining sides are constrained equal in length.
 * Nothing in the construction makes it a rhombus — the numeric solver does, by moving
 * the free vertices until the equations hold.
 */

const p = (x: number, y: number) => vec.vec2(x, y);

const POINT_STYLE: Style = withStyle(DEFAULT_STYLE, {
  pointStyle: PointStyle.Dot,
  pointSize: 7,
});

const TRIANGLE_STYLE: Style = withStyle(DEFAULT_STYLE, {
  stroke: '#1d4ed8',
  strokeWidth: 2,
});

const CIRCLE_STYLE: Style = withStyle(DEFAULT_STYLE, {
  stroke: '#b91c1c',
  strokeWidth: 1.8,
});

const MEDIAN_STYLE: Style = withStyle(CONSTRUCTION_STYLE, {
  stroke: '#0f766e',
  lineStyle: LineStyle.Dashed,
});

const LABEL_STYLE: Style = withStyle(DEFAULT_STYLE, { stroke: '#334155' });

const SOLID_STYLE: Style = withStyle(DEFAULT_STYLE, { stroke: '#0f766e', strokeWidth: 1.6 });

const MARK_STYLE: Style = withStyle(DEFAULT_STYLE, { stroke: '#b91c1c', strokeWidth: 1.4 });

const SOLVED_STYLE: Style = withStyle(DEFAULT_STYLE, {
  stroke: '#7c3aed',
  strokeWidth: 2,
});

export function createDemoDocument(): SceneDocument {
  let doc = createDocument('Triangle, circumcircle and medians');
  let z = 0;

  const add = (raw: string, source: EntitySource, style: Style, label?: string): EntityId => {
    z += 1;
    const id = raw as EntityId;
    doc = addEntity(
      doc,
      createEntity(id, source, style, label === undefined ? { zIndex: z } : { zIndex: z, label })
    );
    return id;
  };

  /* --- three free vertices ------------------------------------------------- */
  const a = add('p1', { op: 'free-point', position: p(-160, -90) }, POINT_STYLE, 'A');
  const b = add('p2', { op: 'free-point', position: p(180, -70) }, POINT_STYLE, 'B');
  const c = add('p3', { op: 'free-point', position: p(20, 160) }, POINT_STYLE, 'C');

  /* --- the triangle itself ------------------------------------------------- */
  add('tri', { op: 'polygon', vertices: [a, b, c] }, TRIANGLE_STYLE);

  /* --- circumcircle: derived, so it is exact at every zoom ------------------ */
  add('circum', { op: 'circle-3-points', a, b, c }, CIRCLE_STYLE);
  const centre = add('circumCentre', { op: 'circumcenter', a, b, c }, POINT_STYLE, 'O');

  /* --- medians and centroid ------------------------------------------------ */
  const midAB = add('mAB', { op: 'midpoint', a, b }, POINT_STYLE);
  const midBC = add('mBC', { op: 'midpoint', a: b, b: c }, POINT_STYLE);
  const midCA = add('mCA', { op: 'midpoint', a: c, b: a }, POINT_STYLE);

  add('medA', { op: 'segment', a: c, b: midAB }, MEDIAN_STYLE);
  add('medB', { op: 'segment', a, b: midBC }, MEDIAN_STYLE);
  add('medC', { op: 'segment', a: b, b: midCA }, MEDIAN_STYLE);
  add('centroid', { op: 'centroid', a, b, c }, POINT_STYLE, 'G');

  /* --- a locus point: exactly on the circle, always ------------------------ */
  add('onCircle', { op: 'locus-point', host: 'circum' as EntityId, t: 0.13 }, POINT_STYLE, 'P');

  /* --- an infinite line, to exercise viewport clipping --------------------- */
  add('euler', { op: 'line', a: centre, b: 'centroid' as EntityId }, MEDIAN_STYLE);

  /* --- a live measurement -------------------------------------------------- */
  add(
    'sideAB',
    { op: 'measurement', measure: 'length', targets: ['medA' as EntityId], offset: p(0, 14) },
    LABEL_STYLE
  );

  /* --- Tier 2: a rhombus the solver produces, off to the right ------------- */
  const q1 = add('q1', { op: 'free-point', position: p(340, -120) }, POINT_STYLE);
  const q2 = add('q2', { op: 'free-point', position: p(520, -95) }, POINT_STYLE);
  const q3 = add('q3', { op: 'free-point', position: p(560, 60) }, POINT_STYLE);
  const q4 = add('q4', { op: 'free-point', position: p(360, 30) }, POINT_STYLE);

  const side12 = add('side12', { op: 'segment', a: q1, b: q2 }, SOLVED_STYLE);
  const side23 = add('side23', { op: 'segment', a: q2, b: q3 }, SOLVED_STYLE);
  const side34 = add('side34', { op: 'segment', a: q3, b: q4 }, SOLVED_STYLE);
  const side41 = add('side41', { op: 'segment', a: q4, b: q1 }, SOLVED_STYLE);

  const constraints: readonly Constraint[] = [
    // Opposite sides parallel: a parallelogram.
    { id: asConstraintId('kp1'), kind: 'parallel', first: side12, second: side34 },
    { id: asConstraintId('kp2'), kind: 'parallel', first: side23, second: side41 },
    // Adjacent sides equal: now a rhombus.
    { id: asConstraintId('ke1'), kind: 'equal-length', first: side12, second: side23 },
    { id: asConstraintId('ke2'), kind: 'equal-length', first: side23, second: side34 },
    // And pin the bottom edge level, so the figure has a definite orientation.
    { id: asConstraintId('kh1'), kind: 'horizontal', entity: side12 },
  ];
  for (const constraint of constraints) doc = addConstraint(doc, constraint);

  /* --- M14: a right angle that stops being one when you drag it ------------ */

  const rA = add('rA', { op: 'free-point', position: p(-330, -40) }, POINT_STYLE);
  const rV = add('rV', { op: 'free-point', position: p(-330, -170) }, POINT_STYLE);
  const rC = add('rC', { op: 'free-point', position: p(-190, -170) }, POINT_STYLE);

  add('rAV', { op: 'segment', a: rA, b: rV }, TRIANGLE_STYLE);
  add('rVC', { op: 'segment', a: rV, b: rC }, TRIANGLE_STYLE);

  /*
   * Placed on an angle that *is* right, so the demo opens showing the square — and dragging
   * `rC` turns it into an arc, which is the behaviour the whole design exists for.
   */
  add(
    'rMark',
    { op: 'angle-mark', a: rA, vertex: rV, c: rC, ticks: 1, showValue: true },
    MARK_STYLE
  );

  /* --- M12: the same cube under two projections ---------------------------- */

  const isoAnchor = add('isoAt', { op: 'free-point', position: p(-330, 190) }, POINT_STYLE);
  add(
    'isoCube',
    {
      op: 'solid',
      solid: 'cube',
      center: isoAnchor,
      width: 130,
      height: 130,
      depth: 130,
      sides: 6,
      taper: 0.5,
      rotation: { x: 0, y: 0, z: 0 },
      projection: 'isometric',
    },
    SOLID_STYLE
  );

  // Deliberately the same cube, drawn differently. Side by side, the two projections are
  // the lesson — which is why the projection belongs to the entity and not the document.
  const cabAnchor = add('cabAt', { op: 'free-point', position: p(-90, 190) }, POINT_STYLE);
  add(
    'cabCube',
    {
      op: 'solid',
      solid: 'cube',
      center: cabAnchor,
      width: 130,
      height: 130,
      depth: 130,
      sides: 6,
      taper: 0.5,
      rotation: { x: 0, y: 0, z: 0 },
      projection: 'cabinet',
    },
    SOLID_STYLE
  );

  return doc;
}
